import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon with merged native + RA count.
 *
 * On every cart:update event the component fetches /cart.js to get the
 * authoritative native item count, eliminating race conditions between
 * delta-based and absolute-based event sources.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} Native Shopify cart item count */
  #nativeCount = 0;

  /** @type {number} RA custom app count */
  #raCount = 0;

  /** @type {ReturnType<typeof setTimeout> | null} Render debounce timer */
  #renderTimer = null;

  /** @type {ReturnType<typeof setTimeout> | null} RA-specific debounce timer */
  #raTimer = null;

  /** @type {AbortController | null} Abort controller for in-flight /cart.js fetches */
  #fetchController = null;

  // ─── Computed merged count ────────────────────────────────────────────────

  get #mergedCount() {
    return this.#nativeCount + this.#raCount;
  }

  // ─── DOM read/write ───────────────────────────────────────────────────────

  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent.trim() ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '99+';
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  connectedCallback() {
    super.connectedCallback();

    this.#nativeCount = parseInt(this.refs.cartBubbleCount.textContent.trim() ?? '0', 10) || 0;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener('RA_PRE_CART_CART_COUNT_FETCHED', this.#onRaCount);
    window.addEventListener('pageshow', this.#onPageShow);

    this.#restoreFromSession();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('RA_PRE_CART_CART_COUNT_FETCHED', this.#onRaCount);
    window.removeEventListener('pageshow', this.#onPageShow);
    clearTimeout(this.#renderTimer);
    clearTimeout(this.#raTimer);
    this.#fetchController?.abort();
  }

  // ─── Event handlers ───────────────────────────────────────────────────────

  /**
   * Handles the page show event when the page is restored from bfcache.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (event.persisted) this.#restoreFromSession();
  };

  /**
   * Handles native Shopify cart updates.
   *
   * Always fetches /cart.js to obtain the authoritative item_count.
   * This avoids the race condition where delta-based sources
   * (product-form, buy-the-bundle, etc.) and absolute-based sources
   * (cart-items-component) can interleave and produce a wrong count.
   *
   * @param {CartUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    const resource = /** @type {{ item_count?: number }} */ (event.detail?.resource);

    if (resource?.item_count != null) {
      this.#nativeCount = resource.item_count;
      this.#scheduleRender();
      return;
    }

    this.#fetchNativeCount();
  };

  /**
   * Fetches /cart.js to get the authoritative native item count.
   * Cancels any in-flight request so only the latest wins.
   */
  async #fetchNativeCount() {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    try {
      const response = await fetch('/cart.js', { signal: this.#fetchController.signal });
      const cart = await response.json();
      this.#nativeCount = cart.item_count ?? 0;
      this.#scheduleRender();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('cart-icon: failed to fetch cart count', error);
      }
    }
  }

  /**
   * Handles the custom RA count event.
   * Uses a debounce so only the last event in a burst is processed.
   * @param {CustomEvent} event
   */
  #onRaCount = (event) => {
    const count = event?.detail?.count ?? event?.detail?.itemCount ?? 0;
    const parsed = Math.max(0, parseInt(count, 10) || 0);

    clearTimeout(this.#raTimer);
    this.#raTimer = setTimeout(() => {
      this.#raCount = parsed;
      this.#scheduleRender();
    }, 300);
  };

  // ─── Debounced render scheduling ──────────────────────────────────────────

  /**
   * Coalesces rapid back-to-back updates (e.g. both events firing on load)
   * into a single DOM write, preventing any count flicker.
   */
  #scheduleRender() {
    clearTimeout(this.#renderTimer);
    this.#renderTimer = setTimeout(() => {
      requestAnimationFrame(() => this.#renderCartBubble());
    }, 0);
  }

  // ─── Core render ──────────────────────────────────────────────────────────

  /**
   * Renders the cart bubble with the merged count.
   * Animates only when the count increases; silent otherwise.
   * @param {boolean} [animate=true]
   */
  #renderCartBubble = async (animate = true) => {
    const merged  = this.#mergedCount;
    const prev    = this.currentCartCount;
    const isEmpty = merged === 0;

    this.refs.cartBubbleCount.classList.toggle('hidden', isEmpty);
    this.refs.cartBubble.classList.toggle('visually-hidden', isEmpty);
    this.classList.toggle('header-actions__cart-icon--has-cart', !isEmpty);

    this.currentCartCount = merged;

    sessionStorage.setItem('cart-count', JSON.stringify({
      value: String(merged),
      timestamp: Date.now(),
    }));
    sessionStorage.setItem('ra-cart-count', String(this.#raCount));

    if (!animate || isEmpty || merged <= prev) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  // ─── Session restore (bfcache / page reload) ──────────────────────────────

  /**
   * Restores counts from sessionStorage after bfcache navigation.
   * Seeds #nativeCount from the DOM first, then applies any recent
   * sessionStorage override if still fresh.
   */
  #restoreFromSession() {
    if (!this.refs.cartBubbleCount) return;

    this.#nativeCount = parseInt(this.refs.cartBubbleCount.textContent.trim() ?? '0', 10) || 0;

    try {
      const stored = sessionStorage.getItem('cart-count');
      if (!stored) return;

      const { value, timestamp } = JSON.parse(stored);
      const visibleCount = this.refs.cartBubbleCount.textContent.trim();

      if (value === visibleCount) return;

      if (Date.now() - timestamp < 10_000) {
        const total = parseInt(value, 10);

        if (total >= 0) {
          const raPart = parseInt(sessionStorage.getItem('ra-cart-count') ?? '0', 10);
          this.#raCount     = isNaN(raPart) ? 0 : raPart;
          this.#nativeCount = Math.max(0, total - this.#raCount);
          this.#renderCartBubble(false);
        }
      }
    } catch (_) {
      // malformed sessionStorage is safe to ignore
    }
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}